import { isNotEmpty, isNotUndefined } from '@newfront-insurance/core';
import { useEffect, useState, type JSX } from 'react';

import { cn } from '../../v2/lib/utils';
import { Field } from '../field';
import { Input } from '../input';
import type { SelectableOption } from '../radio-button';
import { RadioButton } from '../radio-button';
import { Tooltip } from '../tooltip';

// The OtherOption interface defines the configuration for a custom "other" option
// in a radio button group. It allows for customization of the input field that appears
// when the "other" option is selected, including placeholder text, initial value,
// width styling, custom key identifier, and validation error messages
interface OtherOption {
  placeholder?: string;
  value?: string;
  width?: string | number;
  key?: string;
  error?: string;
}

interface Props {
  direction?: 'vertical' | 'horizontal';
  disabled?: boolean;
  label?: string;
  name: string;
  onChange?: (selection: string, otherValue?: string) => unknown;
  options: SelectableOption[];
  value?: string;
  justifyEnd?: boolean;
  gap?: number;
  labelClassName?: string;
  other?: OtherOption | boolean;
}

export * from './util';

export function RadioButtonGroup({
  direction = 'vertical',
  disabled,
  label,
  name,
  onChange,
  options,
  value,
  justifyEnd,
  labelClassName,
  gap,
  other,
}: Props): JSX.Element {
  const [otherText, setOtherText] = useState<string>('');
  const [isOtherSelected, setIsOtherSelected] = useState<boolean>(false);

  const otherOption: OtherOption = typeof other === 'object' ? other : {};
  const otherPlaceholder = otherOption.placeholder || 'Please specify';
  const otherKey = otherOption.key || 'other'; // Default to 'other' if not specified
  const otherError = otherOption.error;

  let otherWidth = '100%';
  if (otherOption.width !== undefined) {
    if (typeof otherOption.width === 'number') {
      otherWidth = `${otherOption.width}px`;
    } else {
      otherWidth = otherOption.width;
    }
  }

  function handleChange(selection: string): void {
    if (onChange) {
      if (selection === otherKey) {
        onChange(selection, otherText);
      } else {
        // Reset other text when a different option is selected
        setOtherText('');
        onChange(selection);
      }
    }
  }

  // Set up the "other" value based on props
  useEffect(() => {
    if (typeof other === 'object' && other.value) {
      setOtherText(other.value);
    }
  }, [other]);

  // Check if the "other" option is currently selected
  useEffect(() => {
    // If current value isn't in the options list, it must be the "other" option
    const isOther = Boolean(other && value && !options.some((option) => option.value.toString() === value));

    setIsOtherSelected(isOther);
  }, [value, options, other]);

  function handleOtherTextChange(text: string): void {
    setOtherText(text);
    if (onChange && isOtherSelected) {
      onChange(otherKey, text);
    }
  }

  const className = `
  flex
  ${direction === 'horizontal' ? 'flex-row' : 'flex-col'}
  ${gap ? `gap-${gap}` : 'gap-2'}
  ${justifyEnd ? 'justify-end' : ''}
  ${direction === 'horizontal' ? 'items-center' : ''}
  w-fit
  `;

  return (
    <div className={cn(className, 'max-w-fit')}>
      {options.map((option: SelectableOption) => {
        const isDisabled = isNotUndefined(option.disabled) ? option.disabled : disabled;
        const radioButtonProps = {
          disabled: isDisabled,
          value: option.value,
          testId: option.testId,
          labelClassName: isNotUndefined(option.labelClassName) ? option.labelClassName : labelClassName,
          inputClassName: option.inputClassName,
          isChecked: value === option.value,
          label: label || option.label,
          name,
          onChange: () => handleChange(option.value.toString()),
        };
        return (
          <div key={`${name}-${option.value}`}>
            {isDisabled && isNotEmpty(option.disabledTooltip) ? (
              <Tooltip message={option.disabledTooltip} positionTip="right" variant="info" offset="10px">
                <RadioButton {...radioButtonProps} />
              </Tooltip>
            ) : (
              <RadioButton {...radioButtonProps} />
            )}
          </div>
        );
      })}

      {other && (
        <div className="flex items-center gap-0">
          <RadioButton
            disabled={disabled}
            isChecked={isOtherSelected}
            label=""
            name={name}
            onChange={() => handleChange(otherKey)}
            value={otherKey}
            testId={`${name}-${otherKey}`}
            labelClassName={labelClassName}
          />
          <div className="grow">
            <Field fieldId={`${name}-${otherKey}-field`} error={otherError} hideEmptyLabel>
              <Input
                type="text"
                placeholder={otherPlaceholder}
                disabled={disabled || !isOtherSelected}
                value={otherText}
                onChange={handleOtherTextChange}
                width={otherWidth}
                fieldId={`${name}-${otherKey}-input`}
                testId={`${name}-${otherKey}-input`}
                isInvalid={Boolean(otherError)}
              />
            </Field>
          </div>
        </div>
      )}
    </div>
  );
}
